import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { eventApi } from '@/services/api';
import { EventListing } from '@/types';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EnquiryPanel } from '@/components/public/EnquiryPanel';
import { DetailPageSkeleton } from '@/components/public/DetailPageSkeleton';
import { PriceDisplay } from '@/components/public/PriceDisplay';

export const EventDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [eventItem, setEventItem] = useState<EventListing | null>(null);
  const [requestStatus, setRequestStatus] = useState<'loading' | 'success' | 'not-found'>('loading');
  const [requestSlug, setRequestSlug] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setEventItem(null);
      setRequestSlug(null);
      setRequestStatus('not-found');
      return;
    }

    const controller = new AbortController();

    const fetchEvent = async () => {
      setRequestSlug(slug);
      setRequestStatus('loading');
      setEventItem(null);
      try {
        const data = await eventApi.getEventBySlug(slug, { signal: controller.signal });
        setEventItem(data ?? null);
        setRequestStatus(data ? 'success' : 'not-found');
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Failed to fetch event:', error);
          setEventItem(null);
          setRequestStatus('not-found');
        }
      }
    };

    void fetchEvent();
    return () => controller.abort();
  }, [slug]);

  if (slug && (requestSlug !== slug || requestStatus === 'loading')) {
    return <DetailPageSkeleton pageClassName="courses-page" />;
  }

  if (requestStatus === 'not-found' || !eventItem) {
    return (
      <section className="courses-page min-h-full">
        <div className="page-shell">
          <Button variant="ghost" size="sm" onClick={() => navigate('/events')}>
            <ArrowLeft className="h-4 w-4" />
            Back to events
          </Button>
          <h1 className="mt-6 text-3xl font-semibold tracking-tight text-slate-950">Event not found</h1>
        </div>
      </section>
    );
  }

  return (
    <section className="courses-page min-h-full">
      <div className="page-shell space-y-8">
        <section className="relative overflow-hidden rounded-[2rem] border border-white/60 bg-slate-950 shadow-[0_30px_80px_rgba(15,23,42,0.16)]">
          <img src={eventItem.coverImageUrl} alt={eventItem.title} className="h-72 w-full object-cover sm:h-80 lg:h-[26rem]" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/42 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 lg:p-10">
            <Button variant="ghost" size="sm" onClick={() => navigate('/events')} className="mb-5 bg-white/10 text-white hover:bg-white/18 hover:text-white">
              <ArrowLeft className="h-4 w-4" />
              Back to events
            </Button>
            <div className="flex flex-wrap gap-2">
              <span className="rounded-full border border-white/16 bg-white/12 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-white">
                {eventItem.category === 'workshop' ? 'Workshop' : 'Event'}
              </span>
              {(eventItem.tags || []).slice(0, 3).map((tag) => (
                <span key={tag} className="rounded-full border border-white/16 bg-white/12 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-white">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl">
              {eventItem.title}
            </h1>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-8 xl:grid-cols-[1.35fr_0.65fr]">
          <div className="space-y-8">
            <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[rgb(var(--accent-solid))]">
                Event Overview
              </p>
              <div className="mt-5 divide-y divide-slate-200">
                <div className="pb-5">
                  <p className="whitespace-pre-line text-base leading-7 text-slate-700">
                    {eventItem.description || 'Full event details will be shared with registered participants.'}
                  </p>
                </div>
                <div className="grid gap-5 py-5 sm:grid-cols-2">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Audience</p>
                    <p className="mt-2 text-base font-semibold text-slate-950">
                      {eventItem.audience?.length ? eventItem.audience.join(', ') : 'Open to interested participants'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Level</p>
                    <p className="mt-2 text-base font-semibold text-slate-950">{eventItem.level || 'Open to All'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Teaching Mode</p>
                    <p className="mt-2 text-base font-semibold text-slate-950">{eventItem.teachingMode || 'Offline'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">When</p>
                    <p className="mt-2 text-base font-semibold text-slate-950">{eventItem.dateText}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Venue</p>
                    <p className="mt-2 text-base font-semibold text-slate-950">
                      {eventItem.venue}, {eventItem.city}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <div className="xl:pt-2">
            <div>
              <EnquiryPanel
                itemType="event"
                itemId={eventItem.id}
                sectionLabel="Learning Guidance"
                ctaLabel="I'm interested"
                submitLabel="Submit"
                showNotesField={false}
                showPrivacyNotes={false}
                revealFieldsOnCta
                extraInfo={{
                  label: eventItem.category === 'workshop' ? 'Workshop Fee' : 'Event Fee',
                  value: <PriceDisplay isFree={eventItem.isFree} originalPrice={eventItem.priceText} variant="detail" />,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
